/**
 * Send the current tab to an external, cross-origin URL.
 *
 * ## Why same-tab, and not `window.open(url, '_blank')`
 *
 * The URLs this serves (today: the Paddle customer portal) are only known
 * *after* an awaited round-trip to our API. Opening a named/blank target from
 * an async continuation is the classic popup-blocked path: `window.open`
 * requires **transient user activation**, which is consumed/expired by the time
 * the fetch resolves — Safari blocks it outright, and Chrome blocks it whenever
 * the request outlives the ~5s activation window. The result is a control that
 * silently does nothing, which is the exact failure we are fixing.
 *
 * The usual workaround — synchronously open `about:blank` on click, then assign
 * `.location.href` when the promise resolves — trades one bug for another: every
 * failure leaves a stray blank tab that must be manually closed, *including* the
 * entirely expected `040501 NoActiveSubscription` (an org that has never billed
 * has no portal). It also still returns `null` when popups are hard-blocked.
 *
 * A **top-level same-tab navigation does not require transient activation and is
 * never popup-blocked**, so it works regardless of how slow the request was. On
 * failure nothing happens at all and the caller renders inline error UI. The
 * billing portal is a leave-and-come-back destination (update a card, resume a
 * subscription, then hit Back), so surrendering SPA state is the right trade.
 *
 * ## Why this is its own module
 *
 * It is also the single seam tests stub: jsdom implements no navigation, and
 * `window.location.assign` is **not** spy-able there — `vi.spyOn` throws
 * `TypeError: Cannot redefine property: assign` (verified on jsdom 29). Routing
 * every external navigation through one named function keeps that mockable in
 * exactly one place, instead of each call site inventing its own escape hatch.
 */
export function navigateExternal(url: string): void {
  window.location.assign(url);
}
